import React, { useState, useEffect } from 'react';
import { MessageCircle, Check, AlertCircle, Copy, Bell, Settings, Users, Zap } from 'lucide-react';
import { telegramBotService } from '../services/telegramBotService';
import Nt from '../components/Nt';

const TelegramIntegration = ({ user, isProfilePage = false }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [connectionStep, setConnectionStep] = useState(1);
    const [connectionCode, setConnectionCode] = useState('');
    const [botInfo, setBotInfo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [notifications, setNotifications] = useState({
        newPartners: false,
        systemUpdates: false,
        achievements: false,
        payments: false
    });
    const [connectionData, setConnectionData] = useState(null);

    useEffect(() => {
        if (user?.id) {
            loadBotInfo();
            checkConnection();
        }
    }, [user]);

    const loadBotInfo = async () => {
        try {
            const response = await telegramBotService.getBotInfo();
            if (response.success) {
                setBotInfo(response.data);
            }
        } catch (error) {
            console.error('Error loading bot info:', error);
        }
    };

    const generateCode = async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await telegramBotService.generateConnectionCode(user.id);
            if (response.success) {
                setConnectionCode(response.data.code);
                setConnectionStep(2);
            } else {
                setError(response.message || 'Ошибка генерации кода');
            }
        } catch (error) {
            setError('Ошибка генерации кода');
        } finally {
            setIsLoading(false);
        }
    };

    const checkConnection = async () => {
        setIsLoading(true);
        try {
            const response = await telegramBotService.checkConnection(user.id);
            if (response.success && response.data.connected) {
                setIsConnected(true);
                setConnectionData(response.data);
                // ИСПРАВЛЯЕМ: правильно загружаем настройки
                if (response.data.settings) {
                    setNotifications(response.data.settings);
                }
            } else {
                setIsConnected(false);
                // Убеждаемся, что бот пишет "Настройки не заданы", убедитесь, что вы отправили код боту.');
            }
        } catch (error) {
            setError('Ошибка проверки подключения');
        } finally {
            setIsLoading(false);
        }
    };

    const disconnect = async () => {
        setIsLoading(true);
        try {
            const response = await telegramBotService.disconnectUser(user.id);
            if (response.success) {
                setIsConnected(false);
                setConnectionStep(1);
                setConnectionCode('');
                setConnectionData(null);
                setError('');
                setNotifications({
                    newPartners: false,
                    systemUpdates: false,
                    achievements: false,
                    payments: false
                });
            }
        } catch (error) {
            setError('Ошибка отключения');
        } finally {
            setIsLoading(false);
        }
    };

    const updateNotificationSettings = async (settings) => {
        try {
            const response = await telegramBotService.updateNotificationSettings(user.id, settings);
            if (response.success) {
                setNotifications(settings);
                // Обновляем данные подключения
                checkConnection();
            }
        } catch (error) {
            console.error('Error updating notification settings:', error);
        }
    };

    const copyBotLink = () => {
        if (botInfo?.username) {
            navigator.clipboard.writeText(`https://t.me/${botInfo.username}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleNotificationToggle = (key) => {
        const newSettings = {
            ...notifications,
            [key]: !notifications[key]
        };
        updateNotificationSettings(newSettings);
    };

    // Получаем активные уведомления для отображения статуса
    const getActiveNotifications = () => {
        const active = [];
        if (notifications.newPartners) active.push('Новые партнеры');
        if (notifications.systemUpdates) active.push('Системные обновления');
        if (notifications.achievements) active.push('Достижения');
        if (notifications.payments) active.push('Выплаты');
        return active;
    };

    if (!isProfilePage) {
        // Компактный виджет для главной страницы
        return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <MessageCircle className="text-blue-500 mr-3" size={24} />
                        <h3 className="text-lg font-semibold text-gray-800">Telegram уведомления</h3>
                    </div>
                    {isConnected && (
                        <div className="flex items-center text-green-600">
                            <Check size={16} className="mr-1" />
                            <span className="text-sm">Подключено</span>
                        </div>
                    )}
                </div>

                {!isConnected ? (
                    <div className="space-y-4">
                        {/* Шаг 1: Начало подключения */}
                        {connectionStep === 1 && (
                            <div className="space-y-4">
                                <p className="text-gray-600 text-sm">
                                    Подключите Telegram бот для получения уведомлений о новых партнерах и важных обновлениях.
                                </p>
                                <button
                                    onClick={generateCode}
                                    disabled={isLoading}
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors disabled:opacity-50 text-sm"
                                >
                                    {isLoading ? 'Генерация...' : 'Подключить Telegram'}
                                </button>
                            </div>
                        )}

                        {/* Шаг 2: Отправка кода */}
                        {connectionStep === 2 && (
                            <div className="space-y-4">
                                <div className="text-center">
                                    <p className="text-gray-600 text-sm mb-2">
                                        1. Перейдите в бот:
                                    </p>
                                    <button
                                        onClick={copyBotLink}
                                        className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 transition-colors mb-3"
                                    >
                                        <Copy size={14} className="mr-1" />
                                        {copied ? 'Скопировано!' : <>@<Nt>{botInfo?.username}</Nt></>}
                                    </button>

                                    <p className="text-gray-600 text-sm mb-2">
                                        2. Отправьте боту этот код:
                                    </p>
                                    <code className="text-lg font-mono font-bold text-blue-600">
                                        {connectionCode}
                                    </code>
                                    <p className="text-xs text-gray-500 mt-1">Код действителен 10 минут</p>
                                </div>

                                <div className="flex space-x-2">
                                    <button
                                        onClick={checkConnection}
                                        disabled={isLoading}
                                        className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded-lg text-sm transition-colors disabled:opacity-50"
                                    >
                                        {isLoading ? 'Проверяем...' : 'Проверить подключение'}
                                    </button>
                                    <button
                                        onClick={() => setConnectionStep(1)}
                                        className="px-3 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
                                    >
                                        Назад
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Статус подключения */}
                        <div className="bg-green-50 border border-green-200 rounded-lg  mb-4">
                            <div className="text-sm">
                                <div className="flex items-center text-green-800 mb-1">
                                    <Check size={14} className="mr-1" />
                                    <span className="font-medium">Подключен к аккаунту: {user.full_name || user.login}</span>
                                </div>
                                {connectionData?.connected_at && (
                                    <div className="text-green-700 text-xs">
                                        📅 Дата подключения: {new Date(connectionData.connected_at).toLocaleString('ru-RU')}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Активные уведомления */}
                        <div>
                            <div className="flex items-center justify-between text-sm font-medium text-gray-700 mb-2">
                                <span>🔔 Активные уведомления:</span>
                            </div>

                            {getActiveNotifications().length > 0 ? (
                                <div className="space-y-1">
                                    {getActiveNotifications().map((notification, index) => (
                                        <div key={index} className="flex items-center text-sm text-green-600">
                                            <Check size={12} className="mr-2" />
                                            <span>{notification}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500">
                                    Настройки не заданы
                                </div>
                            )}
                        </div>

                        <button
                            onClick={() => window.location.href = '/dashboard/profile#telegram'}
                            className="w-full text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors"
                        >
                            Настроить уведомления
                        </button>
                    </div>
                )}

                {error && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
                        <AlertCircle className="text-red-500 mr-2 flex-shrink-0 mt-0.5" size={14} />
                        <p className="text-red-800 text-sm">{error}</p>
                    </div>
                )}
            </div>
        );
    }

    // Полный компонент для страницы профиля
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" id="telegram">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                    <MessageCircle className="text-blue-500 mr-3" size={28} />
                    <div>
                        <h3 className="text-xl font-semibold text-gray-800">Telegram интеграция</h3>
                        <p className="text-gray-600 text-sm">Получайте уведомления прямо в Telegram</p>
                    </div>
                </div>
                {isConnected && (
                    <div className="flex items-center text-green-600 bg-green-50 px-3 py-1 rounded-full">
                        <Check size={16} className="mr-2" />
                        <span className="text-sm font-medium">Активно</span>
                    </div>
                )}
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                    <AlertCircle className="text-red-500 mr-3 flex-shrink-0 mt-0.5" size={16} />
                    <div>
                        <p className="text-red-800 text-sm">{error}</p>
                    </div>
                </div>
            )}

            {!isConnected ? (
                <div className="space-y-6">
                    {/* Преимущества */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
                            <Users className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                            <div>
                                <h4 className="font-medium text-blue-900 mb-1">Новые партнеры</h4>
                                <p className="text-blue-700 text-sm">Мгновенные уведомления о регистрации новых участников в вашей команде</p>
                            </div>
                        </div>
                        <div className="flex items-start space-x-3 p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                            <Zap className="text-emerald-600 flex-shrink-0 mt-0.5" size={20} />
                            <div>
                                <h4 className="font-medium text-emerald-900 mb-1">Системные обновления</h4>
                                <p className="text-emerald-700 text-sm">Важные новости платформы и изменения в системе</p>
                            </div>
                        </div>
                    </div>

                    {/* Процесс подключения */}
                    {connectionStep === 1 && (
                        <div className="text-center space-y-4">
                            <div className="bg-gray-50 rounded-lg p-6">
                                <h4 className="text-lg font-semibold text-gray-800 mb-2">Как подключить?</h4>
                                <div className="space-y-3 text-sm text-gray-600">
                                    <div className="flex items-center justify-center">
                                        <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3">1</div>
                                        <span>Нажмите "Подключить Telegram"</span>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3">2</div>
                                        <span>Перейдите в наш Telegram бот</span>
                                    </div>
                                    <div className="flex items-center justify-center">
                                        <div className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3">3</div>
                                        <span>Отправьте боту полученный код</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={generateCode}
                                disabled={isLoading}
                                className="bg-blue-600 hover:bg-blue-700 text-white py-3 px-8 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? 'Генерация кода...' : 'Подключить Telegram'}
                            </button>
                        </div>
                    )}

                    {connectionStep === 2 && (
                        <div className="max-w-md mx-auto space-y-6">
                            <div className="text-center bg-blue-50 rounded-lg p-6 border border-blue-200">
                                <h4 className="text-lg font-semibold text-blue-900 mb-4">Подключение к боту</h4>

                                <div className="space-y-4">
                                    <div>
                                        <p className="text-blue-800 text-sm mb-2">1. Перейдите в наш Telegram бот:</p>
                                        {botInfo?.username && (
                                            <div className="flex items-center justify-center space-x-2">
                                                <button
                                                    onClick={copyBotLink}
                                                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                                >
                                                    <Copy size={16} className="mr-2" />
                                                    {copied ? 'Скопировано!' : <>@<Nt>{botInfo.username}</Nt></>}
                                                </button>
                                                <a
                                                    href={`https://t.me/${botInfo.username}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                                                >
                                                    Открыть
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <p className="text-blue-800 text-sm mb-2">2. Отправьте боту этот код:</p>
                                        <div className="bg-white border-2 border-blue-300 rounded-lg p-4">
                                            <code className="text-2xl font-mono font-bold text-blue-600">
                                                {connectionCode}
                                            </code>
                                        </div>
                                        <p className="text-blue-600 text-xs mt-2">⏰ Код действителен 10 минут</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex space-x-3">
                                <button
                                    onClick={checkConnection}
                                    disabled={isLoading}
                                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-lg font-medium transition-colors disabled:opacity-50"
                                >
                                    {isLoading ? 'Проверяем подключение...' : 'Проверить подключение'}
                                </button>
                                <button
                                    onClick={() => setConnectionStep(1)}
                                    className="px-4 py-3 text-gray-600 hover:text-gray-800 transition-colors"
                                >
                                    ← Назад
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Статус подключения */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-green-800 mb-3">📊 Статус подключения</h4>
                        <div className="space-y-2 text-sm">
                            <div className="flex items-center text-green-700">
                                <Check size={16} className="mr-2" />
                                <span>Подключен к аккаунту: <strong><Nt>{user.full_name || user.login}</Nt></strong></span>
                            </div>
                            {connectionData?.connected_at && (
                                <div className="text-green-600">
                                    📅 Дата подключения: {new Date(connectionData.connected_at).toLocaleString('ru-RU')}
                                </div>
                            )}
                            <div className="mt-4">
                                <div className="font-medium text-green-800 mb-2">🔔 Активные уведомления:</div>
                                {getActiveNotifications().length > 0 ? (
                                    <div className="grid grid-cols-2 gap-2">
                                        {getActiveNotifications().map((notification, index) => (
                                            <div key={index} className="flex items-center text-green-600 bg-white rounded px-3 py-1 border">
                                                <Check size={14} className="mr-2" />
                                                <span className="text-sm">{notification}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-green-600 bg-white rounded px-3 py-2 border">
                                        <span className="text-sm">Настройки не заданы</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Настройки уведомлений */}
                    <div className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h4 className="text-lg font-semibold text-gray-800">⚙️ Настройки уведомлений</h4>
                            <Settings className="text-gray-500" size={20} />
                        </div>

                        <div className="space-y-4">
                            {[
                                {
                                    key: 'newPartners',
                                    label: 'Новые партнеры',
                                    description: 'Уведомления о регистрации новых участников в вашей команде',
                                    icon: Users
                                },
                                {
                                    key: 'systemUpdates',
                                    label: 'Системные обновления',
                                    description: 'Важные новости платформы и изменения',
                                    icon: Zap
                                },
                                {
                                    key: 'achievements',
                                    label: 'Достижения',
                                    description: 'Награды, статусы и личные достижения',
                                    icon: '🏆'
                                },
                                {
                                    key: 'payments',
                                    label: 'Выплаты',
                                    description: 'Уведомления о начислениях и выплатах',
                                    icon: '💰'
                                }
                            ].map((item) => (
                                <div key={item.key} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                                    <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0">
                                            {typeof item.icon === 'string' ? (
                                                <span className="text-xl">{item.icon}</span>
                                            ) : (
                                                <item.icon className="text-gray-600" size={20} />
                                            )}
                                        </div>
                                        <div>
                                            <h5 className="font-medium text-gray-800">{item.label}</h5>
                                            <p className="text-gray-600 text-sm">{item.description}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={notifications[item.key]}
                                                onChange={() => handleNotificationToggle(item.key)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Действия */}
                    <div className="flex justify-between">
                        <button
                            onClick={disconnect}
                            disabled={isLoading}
                            className="text-red-600 hover:text-red-700 font-medium transition-colors disabled:opacity-50"
                        >
                            Отключить бот
                        </button>

                        <button
                            onClick={checkConnection}
                            disabled={isLoading}
                            className="text-blue-600 hover:text-blue-700 font-medium transition-colors disabled:opacity-50"
                        >
                            {isLoading ? 'Обновление...' : 'Обновить статус'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TelegramIntegration;
